/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import currencyFormatter from 'utils/currencyFormatter';
import CurrencyInput from 'components/CurrencyInput';
import Select from 'components/Select';
import { getMeses, getEstadosGlobales } from 'api/catalogos/index';
import { CheckCircleOutlined } from '@ant-design/icons';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';
import {
  getUmasValores,
  postUmaValor,
  deleteUmaValor,
  patchUmaValor,
  getUmaValor,
} from 'api/catalogos/UmasValores';
import { checkToken } from 'api/utils';
import { FLOAT_NUMER_PATTERN } from 'utils/patterns';

export const umasValoresPermission = {
  permissionModel: 'umavalor',
  catalogsModels: [
    'mes',
    'umageneral',
  ],
  tabsModels: [],
};

const UmaValor = ({
  permission,
  selected,
  disableGrid,
}) => {
  const [form] = Form.useForm();
  const [formDatos] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [meses, setMeses] = useState([]);
  const [estadosGlobales, setEstadosGlobales] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);

  const formInstances = [
    form,
    formDatos,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getUmasValores({ uma: selected });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_meses, _data, _estadosGlobales] = await Promise.all([
        getMeses(),
        getUmasValores({ uma: selected }),
        getEstadosGlobales(),
      ]);
      if (mounted) {
        setMeses(_meses);
        setData(formatReceived(_data));
        setEstadosGlobales(_estadosGlobales);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
    formDatos.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _uma = await getUmaValor(match.id);
    setTimeout(() => {
      if (_uma) {
        form.resetFields();
        formDatos.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_uma));
          formDatos.setFieldsValue(formatReceived(_uma));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formDatos.validateFields();
      const values = {
        ...form.getFieldsValue(),
        ...formDatos.getFieldsValue(),
      };
      const [key] = selectedRowKeys;

      // eslint-disable-next-line max-len
      const saved = await (key ? patchUmaValor(key, { ...values, uma: selected }) : postUmaValor({ ...values, uma: selected }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteUmaValor(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una UMA ya autorizada');
      }
      const vals = {
        ...form.getFieldsValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchUmaValor(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setVisibleAutorizar(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Mes',
      dataIndex: 'mes',
      key: 'mes',
      width: 200,
      render: (val) => meses.find((e) => e.id === val)?.nombre,
    },
    {
      titleText: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      width: 200,
      align: 'left',
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion || 'Sin estado',
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    periodo: [requiredRule],
    valor: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => handleOnFinishConfirmation(),
              permission: { model: 'umavalor', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length
                || data?.find(
                  (e) => e?.id === selectedRowKeys[0],
                )?.estados_globales === ESTATUS_AUTORIZADO
                || data?.find(
                  (e) => e?.id === selectedRowKeys[0],
                )?.estados_globales === ESTATUS_CANCELADO
                || data?.find(
                  (e) => e?.id === selectedRowKeys[0],
                )?.estados_globales === ESTATUS_INACTIVO,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
          disableAdd={disableGrid}
          disableDelete={disableGrid}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Valor UMA`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="35%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            initialValues={{ estados_globales: ESTATUS_ACTIVO }}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={24}>
                <Form.Item
                  name="mes"
                  label="Mes"
                  rules={rules.periodo}
                  hasFeedback
                >
                  <Select
                    labelProp="nombre"
                    keyProp="id"
                    dataSource={meses}
                    disabled={disableGrid}
                  />
                </Form.Item>
                <CurrencyInput
                  name="valor"
                  label="Valor"
                  rules={rules.valor}
                  disabled={disableGrid}
                />
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={[{ required: true }]}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estadosGlobales}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Valor de UMA de ${meses.find((e) => e.id === form.getFieldValue('mes'))?.nombre}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          open={visibleAutorizar}
          contentTitle="¿Seguro que deseas autorizar esta UMA?"
          loading={loading}
        />

      </Spin>
    </Row>
  );
};

export default UmaValor;
